// audio/events.js: audio event bus stub (M2.c-4).
//
// SFX itself is M5. This module exists so the M2.c rendering / animation
// code can emit named events at the right moments, trick won, hand
// over, alone declared, and M5 can plug listeners in without touching
// the call sites.
//
// emit(name, payload?), fires the event to every subscriber. In dev
// mode it also logs to the console so testers can see the timing.
//
// subscribe(name, fn) → unsubscribe(). Listener fn receives the payload.
// reset() clears all listeners (useful for tests).

/** @type {Map<string, Set<Function>>} */
const listeners = new Map();

export function emit(name, payload) {
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    console.info('[audio]', name, payload ?? '');
  }
  const set = listeners.get(name);
  if (!set) return;
  for (const fn of set) {
    try { fn(payload); } catch (err) { console.error('[audio listener]', err); }
  }
}

export function subscribe(name, fn) {
  let set = listeners.get(name);
  if (!set) {
    set = new Set();
    listeners.set(name, set);
  }
  set.add(fn);
  return () => {
    const s = listeners.get(name);
    if (s) s.delete(fn);
  };
}

export function reset() {
  listeners.clear();
}

// Stable event name catalog. Keeping these as constants (not free
// strings) means a typo on the emit side is caught at lint/typecheck.
//
// Hand-outcome events (M2.c-7) are granular per-result so M5 sound
// design can play distinct stings for "made it", "march", "loner
// sweep" and "euchred" without re-deriving the result at the listener.
export const EVENTS = Object.freeze({
  TRICK_WON_YOURS:     'trick_won_yours',
  TRICK_WON_OPPONENTS: 'trick_won_opponents',
  HAND_OVER:           'hand_over',
  HAND_WON_PLUS1:      'hand_won_plus1',
  HAND_WON_PLUS2:      'hand_won_plus2',
  LONER_SWEEP:         'loner_sweep',
  // EUCHRED, makers lost. Per M5 #9 we split by perspective so the
  // descending/ascending samples land on the right ear.
  EUCHRED:             'euchred',          // makers got euchred and they were us
  EUCHRED_THEM:        'euchred_them',     // we euchred the makers (good for us)
  GO_ALONE_DECLARED:   'go_alone_declared',
  BID_SUBMITTED:       'bid_submitted',
});
